// CookKitchenUpdate — the kitchen half of the post-cook flow, one screen.
//
// 2026-06 post-cook collapse. Replaces four phases (ingredientsUsed →
// confirmRemoval → leftovers → leftoverLocation) with a single
// reconcile surface:
//
//   • One row per recipe ingredient, seeded from the mid-cook counter
//     list (checkoffState from buildInitialUsedItems): rows the cook
//     ticked render pre-confirmed as a one-line summary ("−2 cups ·
//     leaves 1.5 cups"); rows they never ticked open flagged "DIDN'T
//     USE?"; if the counter list was never touched, every row opens
//     in the editor like the old screen.
//   • The "leaves N" math renders LIVE per row (buildRemovalPlan is
//     pure and stamps sourceIdx), so the old read-only confirm screen
//     is redundant — review and confirm are the same act.
//   • Unit mismatches block the CTA and force the row's editor open
//     with the unit dropdown right there — no more "TAP BACK TO FIX"
//     dead end, and save() never silently drops a row.
//   • Canonicals this cook empties surface in an "OUT AFTER THIS
//     COOK" strip with one-tap ADD TO LIST chips — closes the
//     deplete→shop loop at the exact moment the user knows they're out.
//   • Leftovers fold in as an inline section at the bottom (yes→where
//     + amount + optional name), not two more screens.
//
// All state lives in CookComplete (the orchestrator) — save() needs
// every field at commit time. This component is layout + row editors.

import { useMemo, useState } from "react";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { findIngredient, unitLabel } from "../data/ingredients";
import { convertWithBridge, formatQty } from "../lib/unitConvert";
import { parseAmountString } from "../lib/nutrition";
import { resolvesToSameCanonical } from "../lib/recipePairing";
import CookCompletePicker from "./CookCompletePicker";

const FRACTIONS = [
  { value: 0.125, label: "⅛" },
  { value: 0.25,  label: "¼" },
  { value: 0.333, label: "⅓" },
  { value: 0.5,   label: "½" },
  { value: 0.667, label: "⅔" },
  { value: 0.75,  label: "¾" },
  { value: 1,     label: "FULL" },
];

const LOCATIONS = [
  { id: "fridge",  emoji: "🧊", label: "Fridge",  hint: "Use within a few days" },
  { id: "freezer", emoji: "❄️", label: "Freezer", hint: "Good for weeks to months" },
  { id: "pantry",  emoji: "🥫", label: "Pantry",  hint: "Shelf-stable only" },
  { id: "garbage", emoji: "🗑️", label: "Garbage", hint: "Tossing the extra — nothing stored" },
];

export default function CookKitchenUpdate({
  recipe,
  usedItems, setUsedItems,
  extraRemovals, setExtraRemovals,
  pantry = [],
  plan = [],
  pickerForIdx, setPickerForIdx,
  // leftover state group (owned by CookComplete)
  leftoverChoice, setLeftoverChoice,
  leftoverLocation, setLeftoverLocation,
  leftoverMode, setLeftoverMode,
  leftoverFraction, setLeftoverFraction,
  leftoverServings, setLeftoverServings,
  leftoverCustomName, setLeftoverCustomName,
  isCompoundProduce,
  manualProduces, setManualProduces,
  producesPickerOpen, setProducesPickerOpen,
  producesSearch, setProducesSearch,
  producesMatches = [],
  // shopping loop closure
  onAddToShopping = null,      // (entry) => void
  shoppingAddedKeys = null,    // Set of canonicalId-or-lowername keys already added
  stepLabel = "",
  onBack,
  onContinue,
  // Log the meal but leave kitchen amounts untouched — the explicit
  // "skip the minus" escape, surfaced in-context here (not just the
  // shell's top-right pill).
  onSkipKitchen = null,
}) {
  const [addLeftoverOpen, setAddLeftoverOpen] = useState(false);
  // Per-row editor expansion. Checked-off rows start collapsed (the
  // cook already told us they used them); everything else starts open.
  // Mismatch rows are forced open regardless (see needsAttention).
  const [expandedOverride, setExpandedOverride] = useState({});

  const setRow = (idx, patch) =>
    setUsedItems(prev => prev.map(r => r.idx === idx ? { ...r, ...patch } : r));

  // plan entries grouped by source row for the live "leaves N" lines.
  const entriesByIdx = useMemo(() => {
    const m = new Map();
    for (const e of plan) {
      if (e.source !== "recipe" || e.sourceIdx == null) continue;
      if (!m.has(e.sourceIdx)) m.set(e.sourceIdx, []);
      m.get(e.sourceIdx).push(e);
    }
    return m;
  }, [plan]);

  const mismatchCount = useMemo(
    () => plan.filter(e => !e.convertible).length,
    [plan],
  );

  // Canonicals this cook fully empties: every plan entry that lands a
  // container on zero with no sealed reserves, where no OTHER stocked
  // row of the same canonical survives. These drive the ADD TO LIST
  // strip. Deduped by canonical (or name for free-text rows).
  const outAfterCook = useMemo(() => {
    const zeroed = plan.filter(e =>
      e.convertible && e.newAmount === 0 && !(Number(e.pantryRow?.reserveCount) > 0));
    const zeroedIds = new Set(zeroed.map(e => e.pantryRowId));
    const out = [];
    const seen = new Set();
    for (const e of zeroed) {
      const cid = e.pantryRow?.ingredientId || null;
      const stillStocked = cid
        ? pantry.some(r =>
            r.id !== e.pantryRowId && !zeroedIds.has(r.id) &&
            Number(r.amount) > 0 && r.ingredientId &&
            resolvesToSameCanonical(r.ingredientId, cid))
        : false;
      if (stillStocked) continue;
      const key = cid || String(e.displayName || "").toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        ingredientId: cid,
        name: e.displayName || e.pantryRow?.name || "Item",
        emoji: e.displayEmoji || null,
        unit: e.pantryRow?.unit || null,
      });
    }
    return out;
  }, [plan, pantry]);

  const fmt = (n) => n == null ? "—" : Number(n.toFixed(n >= 10 ? 1 : 2));
  const isGarbage = leftoverLocation === "garbage";
  const wantsLeftover = leftoverChoice === "yes";

  // ── one recipe-ingredient row ────────────────────────────────────────────
  const renderRow = (row) => {
    const ing = row.canonical;
    const tracked = Boolean(ing);
    const match = tracked && row.selectedRowId
      ? row.matches.find(m => m.id === row.selectedRowId) || null
      : null;
    const extraMatches = tracked ? Math.max(0, row.matches.length - 1) : 0;
    const matchCanon = match?.ingredientId ? findIngredient(match.ingredientId) : null;
    const isSubstitute = !!match && !!ing?.id && !!matchCanon?.id
      && !resolvesToSameCanonical(match.ingredientId, ing.id);
    const emoji = match?.emoji || matchCanon?.emoji || ing?.emoji || row.recipeIng.emoji || "🥣";
    const displayName = match?.name || matchCanon?.name || ing?.name || row.recipeIng.item || "Ingredient";
    const recipeAsk = ing?.name || row.recipeIng.item || null;
    const unitOptions = ing?.units || [];
    const canEditAmount = tracked && row.usedUnit;

    const entries = entriesByIdx.get(row.idx) || [];
    const hasMismatch = entries.some(e => !e.convertible);
    const needsAttention = hasMismatch
      || (!row.skipped && row.checkoffState === "unchecked");
    const expanded = row.skipped ? false
      : hasMismatch ? true
      : expandedOverride[row.idx] != null ? expandedOverride[row.idx]
      : row.checkoffState !== "checked";

    // Collapsed one-liner: what this row does to the kitchen.
    const summaryText = (() => {
      if (row.skipped) return "skipped — kitchen untouched";
      if (!tracked) return "untracked — nothing to update";
      if (!match) return "not in your kitchen — nothing to update";
      if (entries.length === 0) return "no amount set — nothing to update";
      if (hasMismatch) return null; // mismatch banner renders instead
      return entries.map(e => {
        const rowUnitTxt = e.ingredient ? unitLabel(e.ingredient, e.pantryRow.unit) : e.pantryRow.unit;
        const usedTxt = `−${formatQty(e.used, e.ingredient)} ${e.ingredient ? unitLabel(e.ingredient, e.used.unit) : e.used.unit}`;
        return e.newAmount === 0
          ? `${usedTxt} · container clears`
          : `${usedTxt} · leaves ${formatQty({ amount: e.newAmount, unit: e.pantryRow.unit }, e.ingredient)} ${rowUnitTxt}`;
      }).join(" + ");
    })();

    return (
      <div
        key={row.idx}
        style={{
          padding:"12px 14px",
          background: row.skipped ? COLOR.deep : COLOR.ground,
          border: `1px solid ${
            hasMismatch ? COLOR.roseDim
            : row.skipped ? COLOR.soil
            : needsAttention ? COLOR.goldDim
            : (tracked ? COLOR.border : COLOR.edge)}`,
          borderRadius:12,
          opacity: row.skipped ? 0.45 : 1,
          transition:"all 0.15s",
        }}
      >
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:22, flexShrink:0 }}>{emoji}</span>
          <button
            onClick={() => {
              if (row.skipped || hasMismatch) return;
              setExpandedOverride(prev => ({ ...prev, [row.idx]: !expanded }));
            }}
            style={{ flex:1, minWidth:0, background:"transparent", border:"none", padding:0, textAlign:"left", cursor: row.skipped ? "default" : "pointer" }}
          >
            <div style={{ fontFamily:FONT.serif, fontSize:15, color: row.skipped ? COLOR.sub : COLOR.ink, fontStyle:"italic", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              {displayName}
            </div>
            {isSubstitute && recipeAsk && (
              <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.amber, letterSpacing:"0.05em", marginTop:1, fontStyle:"italic" }}>
                SUB FOR {recipeAsk.toUpperCase()}
              </div>
            )}
            <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, marginTop:2, letterSpacing:"0.04em", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              {row.checkoffState === "checked" && !row.skipped && (
                <span style={{ color:COLOR.leaf }}>✓ ADDED DURING COOK</span>
              )}
              {row.checkoffState === "unchecked" && !row.skipped && (
                <span style={{ color:COLOR.gold }}>NOT CHECKED OFF — DID YOU USE IT?</span>
              )}
              {summaryText && <span>{summaryText}</span>}
            </div>
          </button>
          <button
            onClick={() => setRow(row.idx, { skipped: !row.skipped })}
            aria-label={row.skipped ? "Re-include" : "Skip this ingredient"}
            className="mcm-focusable"
            style={{
              width:30, height:30, flexShrink:0,
              background: row.skipped ? COLOR.goldDeep : "transparent",
              color: row.skipped ? COLOR.gold : COLOR.muted,
              border:`1px solid ${row.skipped ? COLOR.goldDim : COLOR.border}`,
              borderRadius:8, cursor:"pointer",
              fontFamily:FONT.mono, fontSize:12, touchAction:"manipulation",
            }}
          >
            {row.skipped ? "↺" : "✕"}
          </button>
        </div>

        {hasMismatch && !row.skipped && (
          <div style={{ marginTop:8, padding:"8px 10px", background:COLOR.roseDeep, border:`1px solid ${COLOR.roseDim}`, borderRadius:8, fontFamily:FONT.mono, fontSize:12, color:COLOR.rose, letterSpacing:"0.04em" }}>
            CAN'T CONVERT {String(match?.unit || "").toUpperCase()} → {String(row.usedUnit || "").toUpperCase()} — PICK A UNIT BELOW OR ✕ SKIP
          </div>
        )}

        {expanded && !row.skipped && (
          <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, letterSpacing:"0.05em", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              {tracked && match && extraMatches > 0 ? (
                <button
                  onClick={() => setPickerForIdx(row.idx)}
                  className="mcm-focusable"
                  style={{
                    padding:"2px 6px", background:COLOR.goldDeep, color:COLOR.gold,
                    border:`1px solid ${COLOR.goldDim}`, borderRadius:4, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:12, letterSpacing:"0.05em", touchAction:"manipulation",
                  }}
                >
                  {`FROM ${(match.location || "pantry").toUpperCase()} · +${extraMatches} MORE ▾`}
                </button>
              ) : (
                <span>
                  {tracked
                    ? (match ? `FROM ${(match.location || "pantry").toUpperCase()}` : "NOT IN YOUR KITCHEN")
                    : "UNTRACKED"}
                </span>
              )}
              <span>· RECIPE: {row.recipeIng.amount || "—"}</span>
            </div>

            {canEditAmount ? (() => {
              const recipeQty = row.recipeIng.qty
                || parseAmountString(row.recipeIng.amount, ing)
                || null;
              const toUsed = (qty) => {
                if (!qty || !row.usedUnit) return null;
                if (qty.unit === row.usedUnit) return Number(qty.amount);
                const res = convertWithBridge(qty, row.usedUnit, ing, match);
                return res.ok ? res.value : null;
              };
              const maxInUsed = match && Number(match.amount) > 0
                ? toUsed({ amount: Number(match.amount), unit: match.unit })
                : null;
              const recipeInUsed = recipeQty ? toUsed(recipeQty) : null;
              // Live-convert when the user flips the unit dropdown so
              // the number tracks the unit (0.5 sticks → 8 tbsp).
              const onUnitChange = (nextUnit) => {
                if (!nextUnit || nextUnit === row.usedUnit) return;
                const curAmt = Number(row.usedAmount);
                if (!Number.isFinite(curAmt) || curAmt <= 0) {
                  setRow(row.idx, { usedUnit: nextUnit });
                  return;
                }
                const res = convertWithBridge(
                  { amount: curAmt, unit: row.usedUnit },
                  nextUnit, ing, match,
                );
                setRow(row.idx, res.ok
                  ? { usedAmount: Number(res.value.toFixed(3)), usedUnit: nextUnit }
                  : { usedUnit: nextUnit }
                );
              };
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"flex-end" }}>
                    <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, letterSpacing:"0.08em" }}>
                      USE:
                    </span>
                    <input
                      type="number" min="0" step="any"
                      value={row.usedAmount ?? ""}
                      onChange={e => setRow(row.idx, { usedAmount: e.target.value === "" ? null : Number(e.target.value) })}
                      style={{ width:64, padding:"6px 8px", background:COLOR.deep, border:`1px solid ${hasMismatch ? COLOR.roseDim : COLOR.border}`, borderRadius:8, fontFamily:FONT.mono, fontSize:13, color:COLOR.ink, textAlign:"right", outline:"none" }}
                    />
                    <select
                      value={row.usedUnit || ""}
                      onChange={e => onUnitChange(e.target.value)}
                      style={{ padding:"6px 4px", background:COLOR.deep, border:`1px solid ${hasMismatch ? COLOR.rose : COLOR.border}`, borderRadius:8, fontFamily:FONT.mono, fontSize:12, color: hasMismatch ? COLOR.rose : COLOR.dim, outline:"none" }}
                    >
                      {unitOptions.map(u => (
                        <option key={u.id} value={u.id}>{unitLabel(ing, u.id)}</option>
                      ))}
                    </select>
                  </div>
                  {!(match && maxInUsed != null && maxInUsed > 0) && (
                    <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.04em", fontStyle:"italic", textAlign:"right" }}>
                      {!match
                        ? "(no kitchen row selected — meter hidden)"
                        : maxInUsed == null
                          ? `(can't convert ${match.unit} → ${row.usedUnit} — meter hidden)`
                          : maxInUsed <= 0
                            ? "(kitchen row shows 0 — meter hidden)"
                            : "(meter unavailable)"}
                    </div>
                  )}
                  {match && maxInUsed != null && maxInUsed > 0 && (() => {
                    // Single bar: LEFT AFTER COOK (solid, grows from 0)
                    // + USED (hatched tail). Slider drives leftAfter so
                    // reading and dragging go the same direction. Same
                    // mechanics as the pre-collapse screen.
                    const pkgQty = match.packageAmount && match.packageUnit
                      ? { amount: Number(match.packageAmount), unit: match.packageUnit }
                      : null;
                    const pkgInUsed = pkgQty ? toUsed(pkgQty) : null;
                    const rowOriginalInUsed = (m) => {
                      const pack = m.packageAmount && m.packageUnit
                        ? (m.packageUnit === row.usedUnit
                            ? Number(m.packageAmount)
                            : (convertWithBridge(
                                { amount: Number(m.packageAmount), unit: m.packageUnit },
                                row.usedUnit, ing, m,
                              ).value))
                        : null;
                      if (Number.isFinite(pack) && pack > 0) return pack;
                      const max = Number(m.max);
                      if (Number.isFinite(max) && max > 0) {
                        if (m.unit === row.usedUnit) return max;
                        const res = convertWithBridge(
                          { amount: max, unit: m.unit },
                          row.usedUnit, ing, m,
                        );
                        return res.ok ? res.value : null;
                      }
                      if (m.unit === row.usedUnit) return Number(m.amount);
                      const res = convertWithBridge(
                        { amount: Number(m.amount), unit: m.unit },
                        row.usedUnit, ing, m,
                      );
                      return res.ok ? res.value : null;
                    };
                    const rowCurrentInUsed = (m) => {
                      if (m.unit === row.usedUnit) return Number(m.amount);
                      const res = convertWithBridge(
                        { amount: Number(m.amount), unit: m.unit },
                        row.usedUnit, ing, m,
                      );
                      return res.ok ? res.value : 0;
                    };
                    const stockOriginal = (row.matches || []).reduce((sum, m) => {
                      const v = rowOriginalInUsed(m);
                      return sum + (Number.isFinite(v) && v > 0 ? v : 0);
                    }, 0);
                    const stockCurrent = (row.matches || []).reduce((sum, m) => {
                      const v = rowCurrentInUsed(m);
                      return sum + (Number.isFinite(v) && v > 0 ? v : 0);
                    }, 0);
                    const pantryNow = Math.max(maxInUsed, stockCurrent);
                    const meterMax  = Math.max(pantryNow, stockOriginal);
                    const packageCount = (pkgInUsed && pkgInUsed > 0)
                      ? Math.max(1, Math.round(meterMax / pkgInUsed))
                      : 0;
                    const step = meterMax <= 10 ? 0.1 : meterMax <= 100 ? 1 : meterMax / 100;
                    const cur = Number.isFinite(Number(row.usedAmount))
                      ? Math.min(Number(row.usedAmount), pantryNow)
                      : 0;
                    const leftAfter = Math.max(0, pantryNow - cur);
                    const pct = (v) => Math.max(0, Math.min(100, (v / meterMax) * 100));
                    const unitTxt = unitLabel(ing, row.usedUnit);
                    const leftRatio = pantryNow > 0 ? leftAfter / pantryNow : 0;
                    const leftColor = leftRatio >= 0.6 ? COLOR.leafMuted
                                    : leftRatio >= 0.25 ? COLOR.gold
                                    : "#c05a44";
                    const leftPct = pct(leftAfter);
                    const usedPct = pct(cur);
                    return (
                      <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:6 }}>
                        <div style={{ fontFamily:FONT.mono, fontSize:12, letterSpacing:"0.04em", color:leftColor }}>
                          {fmt(leftAfter)} {unitTxt} LEFT AFTER COOK
                        </div>
                        <div style={{ position:"relative", width:"100%", height:22 }}>
                          <div style={{
                            position:"absolute", top:5, left:0, right:0, height:12,
                            background:COLOR.border, borderRadius:6,
                          }} />
                          <div style={{
                            position:"absolute", top:5, left:0, height:12,
                            width:`${leftPct}%`,
                            background: leftColor,
                            borderRadius: leftPct >= 99.5 ? 6 : "6px 0 0 6px",
                            transition:"width 0.08s linear, background 0.15s linear",
                          }} />
                          <div style={{
                            position:"absolute", top:5, left:`${leftPct}%`, height:12,
                            width:`${usedPct}%`,
                            background: `repeating-linear-gradient(
                              -45deg,
                              ${leftColor}66 0px,
                              ${leftColor}66 6px,
                              ${leftColor}22 6px,
                              ${leftColor}22 12px
                            )`,
                            backgroundSize: "17px 17px",
                            animation: "cookCompleteDelta 1.8s linear infinite",
                            borderRadius:
                              (leftPct + usedPct) >= 99.5 && leftPct < 0.5 ? 6
                              : (leftPct + usedPct) >= 99.5 ? "0 6px 6px 0"
                              : 0,
                            transition:"width 0.08s linear, left 0.08s linear, background 0.15s linear",
                          }} />
                          <div style={{
                            position:"absolute",
                            top:2, left:`calc(${leftPct}% - 9px)`,
                            width:18, height:18,
                            background: leftColor,
                            border:`3px solid ${COLOR.deep}`,
                            borderRadius:"50%",
                            pointerEvents:"none",
                            boxShadow:`0 0 6px ${leftColor}66`,
                            transition:"background 0.15s linear, box-shadow 0.15s linear, left 0.08s linear",
                          }} />
                          <input
                            type="range"
                            min="0" max={pantryNow} step={step}
                            value={leftAfter}
                            onChange={e => {
                              const nextLeft = Number(e.target.value);
                              const nextUsed = Math.max(0, pantryNow - nextLeft);
                              setRow(row.idx, {
                                usedAmount: nextUsed,
                                usedUnit: row.usedUnit,
                              });
                            }}
                            aria-label={`Estimate ${displayName} used`}
                            style={{
                              position:"absolute", inset:0,
                              width: meterMax > 0
                                ? `${(pantryNow / meterMax) * 100}%`
                                : "100%",
                              margin:0,
                              opacity:0.01,
                              cursor:"pointer",
                            }}
                          />
                        </div>
                        <div style={{
                          display:"flex", justifyContent:"space-between",
                          fontFamily:FONT.mono, fontSize:12,
                          letterSpacing:"0.04em", color:COLOR.muted,
                        }}>
                          <span style={{ color:COLOR.leafMuted }}>
                            IN KITCHEN: {(pkgInUsed && pkgInUsed > 0 && packageCount > 1)
                              ? `${fmt(pkgInUsed)} × ${packageCount} PACKS`
                              : `${fmt(pantryNow)} ${unitTxt}`}
                          </span>
                          {recipeInUsed != null && (
                            <span style={{ color:CHIP_TONES.canonical.fg }}>
                              RECIPE: {fmt(recipeInUsed)} {unitTxt}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })() : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"40px 24px 32px", overflowY:"auto" }}>
      <style>{`
        @keyframes cookCompleteDelta {
          from { background-position: 0 0; }
          to   { background-position: 34px 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="cookCompleteDelta"] {
            animation: none !important;
          }
        }
      `}</style>
      {stepLabel && (
        <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:8 }}>
          {stepLabel}
        </div>
      )}
      <h2 style={{ fontFamily:FONT.serif, fontSize:28, fontWeight:300, fontStyle:"italic", color:COLOR.ink, marginBottom:6 }}>
        Update your kitchen
      </h2>
      <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.muted, marginBottom:20 }}>
        {usedItems.some(r => r.checkoffState !== "none")
          ? "Pre-filled from what you checked off while cooking. Tap a row to adjust, ✕ anything you didn't use."
          : "Each row shows what we'll take off your kitchen. Tap to adjust, ✕ anything you didn't use."}
      </p>

      <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:18 }}>
        {usedItems.map(renderRow)}

        {/* Extra removals — kitchen rows the user decremented beyond
            the recipe's list (leftover chicken, homemade sauce). */}
        {extraRemovals.map(extra => (
          <div
            key={extra.tempId}
            style={{
              display:"flex", alignItems:"center", gap:10,
              padding:"12px 14px",
              background:"#0f140a",
              border:`1px solid ${COLOR.leafDim}`,
              borderRadius:12,
            }}
          >
            <span style={{ fontSize:22, flexShrink:0 }}>{extra.emoji || "🥣"}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:FONT.serif, fontSize:15, color:"#d4ebd4", fontStyle:"italic", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {extra.name}
              </div>
              <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.leafMuted, marginTop:2, letterSpacing:"0.05em" }}>
                + ADDED · FROM {(extra.location || "pantry").toUpperCase()}
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }}>
              <input
                type="number" min="0" step="any"
                value={extra.amount ?? ""}
                onChange={e => {
                  const v = e.target.value === "" ? null : Number(e.target.value);
                  setExtraRemovals(prev => prev.map(x => x.tempId === extra.tempId ? { ...x, amount: v } : x));
                }}
                style={{ width:56, padding:"6px 8px", background:COLOR.deep, border:`1px solid ${COLOR.leafDim}`, borderRadius:8, fontFamily:FONT.mono, fontSize:13, color:"#d4ebd4", textAlign:"right", outline:"none" }}
              />
              <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.leafMuted, minWidth:38 }}>
                {extra.unitLabel}
              </span>
            </div>
            <button
              onClick={() => setExtraRemovals(prev => prev.filter(x => x.tempId !== extra.tempId))}
              aria-label="Remove from list"
              className="mcm-focusable"
              style={{ width:30, height:30, flexShrink:0, background:"transparent", color:COLOR.muted, border:`1px solid ${COLOR.border}`, borderRadius:8, cursor:"pointer", fontFamily:FONT.mono, fontSize:12, touchAction:"manipulation" }}
            >
              ✕
            </button>
          </div>
        ))}

        <button
          onClick={() => setAddLeftoverOpen(true)}
          disabled={!pantry || pantry.length === 0}
          className="mcm-focusable"
          style={{
            padding:"12px 14px",
            background:"transparent",
            border:`1px dashed ${COLOR.border}`,
            borderRadius:12,
            fontFamily:FONT.mono, fontSize:12,
            color: pantry && pantry.length > 0 ? COLOR.dim : COLOR.sub,
            letterSpacing:"0.08em",
            cursor: pantry && pantry.length > 0 ? "pointer" : "not-allowed",
            textAlign:"left", touchAction:"manipulation",
          }}
        >
          + ADD LEFTOVER / SUB
        </button>
      </div>

      {/* Out after this cook — the deplete→shop loop closure. One tap
          re-adds the emptied canonical to the shopping list. */}
      {outAfterCook.length > 0 && onAddToShopping && (
        <div style={{ marginBottom:18, padding:"12px 14px", background:COLOR.deep, border:`1px solid ${COLOR.goldDim}`, borderRadius:12 }}>
          <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, letterSpacing:"0.12em", marginBottom:8 }}>
            OUT AFTER THIS COOK
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {outAfterCook.map(item => {
              const added = shoppingAddedKeys?.has(item.key);
              return (
                <button
                  key={item.key}
                  onClick={() => { if (!added) onAddToShopping(item); }}
                  disabled={added}
                  className="mcm-focusable"
                  style={{
                    display:"flex", alignItems:"center", gap:6,
                    padding:"7px 12px",
                    background: added ? COLOR.leafDeep : COLOR.ground,
                    border:`1px solid ${added ? COLOR.leafDim : COLOR.border}`,
                    borderRadius:999,
                    fontFamily:FONT.mono, fontSize:12,
                    color: added ? COLOR.leaf : COLOR.ink,
                    letterSpacing:"0.04em",
                    cursor: added ? "default" : "pointer",
                    touchAction:"manipulation",
                  }}
                >
                  {added ? "✓" : "+"} {item.name.toUpperCase()}{added ? " · ON LIST" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Leftovers — inline, no extra screens. Defaults to the fast
          path; compound-produce recipes default to saving. */}
      <div style={{ marginBottom:18, padding:"14px", background:COLOR.ground, border:`1px solid ${wantsLeftover ? COLOR.leafDim : COLOR.border}`, borderRadius:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: wantsLeftover ? 12 : 0 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:FONT.serif, fontSize:17, color:COLOR.ink, fontStyle:"italic" }}>
              {isCompoundProduce ? "Bottle this up?" : "Leftovers?"}
            </div>
            <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, marginTop:2, letterSpacing:"0.04em" }}>
              {isCompoundProduce ? "SAVE THE BATCH AS A KITCHEN INGREDIENT" : "SAVE A PORTION FOR A FUTURE MEAL"}
            </div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            {[{ id:"yes", label: isCompoundProduce ? "SAVE" : "YES" }, { id:"no", label: isCompoundProduce ? "USED IT" : "NO" }].map(opt => {
              const active = leftoverChoice === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setLeftoverChoice(opt.id)}
                  className="mcm-focusable"
                  style={{
                    padding:"9px 16px",
                    background: active ? (opt.id === "yes" ? COLOR.leafDeep : COLOR.goldDeep) : COLOR.soil,
                    border:`1px solid ${active ? (opt.id === "yes" ? COLOR.leaf : COLOR.gold) : COLOR.border}`,
                    color: active ? (opt.id === "yes" ? COLOR.leaf : COLOR.gold) : COLOR.dim,
                    borderRadius:10, cursor:"pointer",
                    fontFamily:FONT.mono, fontSize:12, fontWeight:600,
                    letterSpacing:"0.08em", touchAction:"manipulation",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {wantsLeftover && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:8, marginBottom:12 }}>
              {LOCATIONS.map(loc => {
                const active = leftoverLocation === loc.id;
                return (
                  <button
                    key={loc.id}
                    onClick={() => setLeftoverLocation(loc.id)}
                    className="mcm-focusable"
                    style={{
                      padding:"10px 12px",
                      background: active ? COLOR.goldDeep : COLOR.soil,
                      border:`1px solid ${active ? COLOR.gold : COLOR.border}`,
                      borderRadius:10, cursor:"pointer", textAlign:"left",
                      display:"flex", alignItems:"center", gap:8, touchAction:"manipulation",
                    }}
                  >
                    <span style={{ fontSize:20 }}>{loc.emoji}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontFamily:FONT.serif, fontSize:14, color: active ? COLOR.gold : COLOR.ink, fontStyle:"italic" }}>
                        {loc.label}
                      </div>
                      <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, marginTop:1, letterSpacing:"0.02em", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {loc.hint}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {leftoverLocation && !isGarbage && (
              <>
                <div style={{ display:"flex", gap:8, marginBottom:10, background:COLOR.deep, border:`1px solid ${COLOR.edge}`, borderRadius:10, padding:3 }}>
                  {["fraction","servings"].map(mode => {
                    const active = leftoverMode === mode;
                    return (
                      <button
                        key={mode}
                        onClick={() => setLeftoverMode(mode)}
                        className="mcm-focusable"
                        style={{
                          flex:1, padding:"8px",
                          background: active ? COLOR.goldDeep : "transparent",
                          color: active ? COLOR.gold : COLOR.muted,
                          border:"none", borderRadius:8,
                          fontFamily:FONT.mono, fontSize:12, fontWeight:600,
                          letterSpacing:"0.08em", cursor:"pointer", touchAction:"manipulation",
                        }}
                      >
                        {mode === "fraction" ? "BY FRACTION" : "BY SERVINGS"}
                      </button>
                    );
                  })}
                </div>

                {leftoverMode === "fraction" ? (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(7, 1fr)", gap:6, marginBottom:8 }}>
                    {FRACTIONS.map(f => {
                      const active = Math.abs(leftoverFraction - f.value) < 0.01;
                      return (
                        <button
                          key={f.value}
                          onClick={() => setLeftoverFraction(f.value)}
                          className="mcm-focusable"
                          style={{
                            padding:"10px 2px",
                            background: active ? COLOR.goldDeep : COLOR.soil,
                            border:`1px solid ${active ? COLOR.gold : COLOR.border}`,
                            borderRadius:8,
                            fontFamily:FONT.serif, fontSize: f.label === "FULL" ? 12 : 17,
                            color: active ? COLOR.gold : COLOR.ink,
                            cursor:"pointer", touchAction:"manipulation",
                          }}
                        >
                          {f.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:10, marginBottom:8 }}>
                    <input
                      type="number" min="0" step="0.25"
                      value={leftoverServings}
                      onChange={e => setLeftoverServings(Math.max(0, Number(e.target.value) || 0))}
                      style={{ flex:1, padding:"8px 12px", background:COLOR.deep, border:`1px solid ${COLOR.border}`, borderRadius:8, fontFamily:FONT.mono, fontSize:16, color:COLOR.gold, textAlign:"center", outline:"none" }}
                    />
                    <div style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.dim }}>
                      serving{leftoverServings === 1 ? "" : "s"}
                    </div>
                  </div>
                )}

                <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.08em", textAlign:"center", marginBottom:4 }}>
                  {leftoverMode === "fraction"
                    ? `≈ ${Number((recipe?.serves || 2) * leftoverFraction).toFixed(2)} servings saved`
                    : `${leftoverServings} of ${recipe?.serves || "?"} servings saved`}
                </div>

                {!isCompoundProduce && (
                  <div style={{ marginTop:10 }}>
                    <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, letterSpacing:"0.1em", marginBottom:6 }}>
                      CALL IT SOMETHING ELSE? (OPTIONAL)
                    </div>
                    <input
                      type="text"
                      value={leftoverCustomName}
                      onChange={e => setLeftoverCustomName(e.target.value)}
                      placeholder={`Leftover ${recipe.title}`}
                      maxLength={80}
                      style={{
                        width:"100%", boxSizing:"border-box",
                        padding:"10px 14px",
                        background:COLOR.deep, border:`1px solid ${COLOR.border}`,
                        borderRadius:10, color:COLOR.ink,
                        fontFamily:FONT.serif, fontStyle:"italic",
                        fontSize:15, outline:"none",
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Manual canonical-bind — "this output IS a kitchen ingredient
            (dashi, stock, sauce)". Same behavior as the old leftovers
            screen, folded in. */}
        {!isCompoundProduce && !producesPickerOpen && wantsLeftover && (
          <button
            onClick={() => setProducesPickerOpen(true)}
            className="mcm-focusable"
            style={{ marginTop:10, width:"100%", boxSizing:"border-box", padding:"10px 14px", background:"transparent", border:`1px dashed ${COLOR.border}`, borderRadius:10, fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, cursor:"pointer", letterSpacing:"0.06em", textAlign:"left", touchAction:"manipulation" }}
          >
            + IS THIS A KITCHEN INGREDIENT? (dashi, stock, sauce…)
          </button>
        )}
        {isCompoundProduce && manualProduces && (
          <div style={{ marginTop:10, padding:"10px 14px", background:COLOR.leafDeep, border:`1px solid ${COLOR.leafDim}`, borderRadius:10, display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, fontFamily:FONT.mono, fontSize:12, color:COLOR.leaf, letterSpacing:"0.05em" }}>
              SAVING AS · {manualProduces.name?.toUpperCase()}
            </div>
            <button
              onClick={() => { setManualProduces(null); setProducesSearch(""); }}
              className="mcm-focusable"
              style={{ background:"transparent", border:"none", color:COLOR.leafMuted, fontFamily:FONT.mono, fontSize:12, letterSpacing:"0.08em", cursor:"pointer", touchAction:"manipulation" }}
            >
              CLEAR
            </button>
          </div>
        )}
        {producesPickerOpen && !manualProduces && (
          <div style={{ marginTop:10, padding:"12px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <input
                autoFocus
                value={producesSearch}
                onChange={(e) => setProducesSearch(e.target.value)}
                placeholder="dashi, chicken stock, pesto…"
                style={{ flex:1, padding:"10px 12px", background:COLOR.deep, border:`1px solid ${COLOR.border}`, borderRadius:8, fontFamily:FONT.sans, fontSize:14, color:COLOR.ink, outline:"none" }}
              />
              <button
                onClick={() => { setProducesPickerOpen(false); setProducesSearch(""); }}
                className="mcm-focusable"
                style={{ padding:"8px 12px", background:"transparent", border:"none", color:COLOR.dim, fontFamily:FONT.mono, fontSize:12, cursor:"pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}
              >
                CANCEL
              </button>
            </div>
            {producesMatches.length > 0 ? (
              <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:240, overflowY:"auto" }}>
                {producesMatches.map((pIng) => (
                  <button
                    key={pIng.id}
                    onClick={() => {
                      setManualProduces({
                        id: pIng.id,
                        name: pIng.name,
                        defaultUnit: pIng.defaultUnit || pIng.units?.[0]?.id || "cup",
                      });
                      setProducesPickerOpen(false);
                    }}
                    className="mcm-focusable"
                    style={{ padding:"10px 12px", background:COLOR.deep, border:`1px solid ${COLOR.border}`, borderRadius:8, fontFamily:FONT.sans, fontSize:14, color:COLOR.ink, cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:8, touchAction:"manipulation" }}
                  >
                    <span style={{ fontSize:18 }}>{pIng.emoji || "🥣"}</span>
                    <span style={{ flex:1 }}>{pIng.name}</span>
                    <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.05em" }}>{pIng.category}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ padding:"12px", fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.05em", textAlign:"center" }}>
                {producesSearch.trim().length < 2
                  ? "TYPE AT LEAST 2 LETTERS"
                  : "NO MATCH — TRY ANOTHER NAME"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mismatches DON'T block anymore — a unit that can't convert
          ("impossible measurement") is simply left untouched on
          commit (CookComplete skips non-convertible entries), so the
          cook is never trapped. We just say so, transparently. Only a
          missing leftover spot (a real un-fillable choice) gates the
          CTA. */}
      {mismatchCount > 0 && (
        <div style={{ marginTop:"auto", padding:"8px 12px", background:COLOR.soil, border:`1px solid ${COLOR.goldDim}`, borderRadius:10, fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, lineHeight:1.4 }}>
          {mismatchCount} item{mismatchCount === 1 ? "" : "s"} can't auto-convert — they'll be left untouched. Pick a unit above to deduct them, or just continue.
        </div>
      )}
      <div style={{ display:"flex", gap:10, marginTop: mismatchCount > 0 ? 10 : "auto" }}>
        <button onClick={onBack}
          className="mcm-focusable"
          style={{ flex:1, padding:"14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, cursor:"pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}>
          ← BACK
        </button>
        <button
          onClick={onContinue}
          disabled={wantsLeftover && !leftoverLocation}
          className="mcm-focusable"
          style={{
            flex:2, padding:"14px",
            background: (wantsLeftover && !leftoverLocation) ? COLOR.soil : COLOR.leaf,
            border:"none", borderRadius:12,
            fontFamily:FONT.mono, fontSize:12, fontWeight:600,
            color: (wantsLeftover && !leftoverLocation) ? COLOR.sub : COLOR.night,
            cursor: (wantsLeftover && !leftoverLocation) ? "not-allowed" : "pointer",
            letterSpacing:"0.08em", touchAction:"manipulation",
          }}>
          {wantsLeftover && !leftoverLocation
            ? "PICK A LEFTOVER SPOT ↑"
            : "UPDATE KITCHEN →"}
        </button>
      </div>
      {/* In-context "skip the minus" — the very specific request: leave
          kitchen amounts untouched without hunting for the top-right
          pill. Quiet text link so it doesn't compete with UPDATE. */}
      {onSkipKitchen && (
        <button
          type="button"
          onClick={onSkipKitchen}
          className="mcm-focusable"
          style={{ marginTop:10, alignSelf:"center", background:"transparent", border:"none", color:COLOR.dim, fontFamily:FONT.forma, fontWeight:600, fontSize:12, textDecoration:"underline", textUnderlineOffset:3, textDecorationColor:COLOR.border, cursor:"pointer", touchAction:"manipulation" }}>
          Skip kitchen — leave my amounts alone, just rate it →
        </button>
      )}

      {/* Add-leftover / substitution picker sheet — unchanged behavior
          from the pre-collapse screen, kitchen rows grouped by
          location, FIFO by expiry. */}
      {addLeftoverOpen && (
        <div
          onClick={() => setAddLeftoverOpen(false)}
          style={{ position:"absolute", inset:0, background:"#000d", zIndex:6, display:"flex", alignItems:"flex-end", animation:"rise 0.18s ease" }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width:"100%", maxHeight:"80%", overflowY:"auto", background:COLOR.deep, borderTop:`1px solid ${COLOR.border}`, borderTopLeftRadius:18, borderTopRightRadius:18, padding:"22px 20px 16px" }}
          >
            <div style={{ width:42, height:4, background:COLOR.border, borderRadius:2, margin:"0 auto 16px" }} />
            <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:6 }}>
              ADD LEFTOVER / SUB
            </div>
            <p style={{ fontFamily:FONT.sans, fontSize:12, color:COLOR.muted, marginBottom:16 }}>
              Used something the recipe didn't call for — yesterday's cooked chicken, a homemade sauce? Pick it here and we'll decrement it too.
            </p>

            {["fridge","freezer","pantry"].map(loc => {
              const rowsAtLoc = (pantry || [])
                .filter(p => (p.location || "pantry") === loc && Number(p.amount) > 0)
                .sort((a, b) => {
                  const ax = a.expiresAt ? a.expiresAt.getTime() : Infinity;
                  const bx = b.expiresAt ? b.expiresAt.getTime() : Infinity;
                  return ax - bx;
                });
              if (rowsAtLoc.length === 0) return null;
              return (
                <div key={loc} style={{ marginBottom:14 }}>
                  <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.15em", marginBottom:8 }}>
                    {loc.toUpperCase()}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {rowsAtLoc.map(p => {
                      const canonical = p.ingredientId ? findIngredient(p.ingredientId) : null;
                      const uLabel = canonical ? unitLabel(canonical, p.unit) : p.unit;
                      const alreadyAdded = extraRemovals.some(x => x.pantryRowId === p.id);
                      const isMeal = (p.kind || "ingredient") === "meal";
                      return (
                        <button
                          key={p.id}
                          disabled={alreadyAdded}
                          onClick={() => {
                            // Default the amount to the recipe's ask
                            // when this row's canonical matches a
                            // recipe ingredient (converted into the
                            // row's unit); otherwise 1 of the row's
                            // unit.
                            let defaultAmount = 1;
                            if (p.ingredientId && canonical) {
                              const matchRow = usedItems.find(u =>
                                u.canonical && resolvesToSameCanonical(p.ingredientId, u.canonical.id));
                              const ask = matchRow
                                ? (matchRow.recipeIng.qty
                                    || parseAmountString(matchRow.recipeIng.amount, matchRow.canonical))
                                : null;
                              if (ask && Number(ask.amount) > 0) {
                                if (ask.unit === p.unit) {
                                  defaultAmount = Number(ask.amount);
                                } else {
                                  const res = convertWithBridge(ask, p.unit, canonical, p);
                                  if (res.ok && res.value > 0) defaultAmount = Number(res.value.toFixed(3));
                                }
                              }
                            }
                            setExtraRemovals(prev => [...prev, {
                              tempId: `extra-${p.id}-${Date.now()}`,
                              pantryRowId: p.id,
                              ingredientId: p.ingredientId || null,
                              name: p.name,
                              emoji: p.emoji,
                              amount: defaultAmount,
                              unit: p.unit,
                              unitLabel: uLabel,
                              location: p.location || "pantry",
                              kind: p.kind || "ingredient",
                            }]);
                            setAddLeftoverOpen(false);
                          }}
                          style={{
                            textAlign:"left", padding:"10px 12px",
                            background: alreadyAdded ? COLOR.deep : COLOR.ground,
                            border:`1px solid ${alreadyAdded ? COLOR.soil : COLOR.border}`,
                            borderRadius:10,
                            cursor: alreadyAdded ? "not-allowed" : "pointer",
                            opacity: alreadyAdded ? 0.45 : 1,
                            display:"flex", alignItems:"center", gap:10,
                          }}
                        >
                          <span style={{ fontSize:20 }}>{p.emoji || "🥣"}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontFamily:FONT.serif, fontSize:14, color:COLOR.ink, fontStyle:"italic", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {p.name}
                              {isMeal && <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.leafMuted, marginLeft:8, letterSpacing:"0.1em" }}>LEFTOVER</span>}
                            </div>
                            <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, marginTop:2 }}>
                              {p.amount} {uLabel}
                            </div>
                          </div>
                          {alreadyAdded && <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, letterSpacing:"0.1em" }}>ADDED</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <button
              onClick={() => setAddLeftoverOpen(false)}
              className="mcm-focusable"
              style={{ marginTop:4, width:"100%", padding:"12px", background:"transparent", border:`1px solid ${COLOR.border}`, borderRadius:10, fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, cursor:"pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}
            >
              CLOSE
            </button>
          </div>
        </div>
      )}

      <CookCompletePicker
        pickerForIdx={pickerForIdx}
        setPickerForIdx={setPickerForIdx}
        usedItems={usedItems}
        setUsedItems={setUsedItems}
      />
    </div>
  );
}
